/**
 * Payment Result Page - עמוד תוצאת תשלום
 * Landing page after the user returns from CardCom.
 *
 * The redirect parameters from CardCom are NOT trusted — the source of truth is
 * the webhook that CardCom hits server-to-server. This page polls our backend
 * until it sees the user's plan flip to 'pro' (or timeout).
 */

import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react'
import api from '../services/api'
import { useAuthStore } from '../store/authStore'
import type { SubscriptionStatusResponse } from '../types'

const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 30_000

type ResultState = 'pending' | 'success' | 'failed' | 'timeout'

export default function PaymentResultPage() {
  const [searchParams] = useSearchParams()
  const initialStatus = searchParams.get('status') // 'success' | 'failed' | null
  const [state, setState] = useState<ResultState>(
    initialStatus === 'failed' ? 'failed' : 'pending'
  )
  const { updateUser } = useAuthStore()

  useEffect(() => {
    if (state !== 'pending') return

    const startedAt = Date.now()
    let cancelled = false

    const poll = async () => {
      try {
        const res = await api.get<SubscriptionStatusResponse>('/payment/subscription')
        if (cancelled) return

        if (res.data.plan === 'pro') {
          // Refresh the stored user so plan + subscriptionDecisionAt are current
          // (clears the onboarding gate so the user isn't re-prompted).
          try {
            const me = await api.get('/auth/me')
            if (me.data?.user) updateUser(me.data.user)
          } catch {
            /* non-fatal */
          }
          setState('success')
          return
        }

        if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
          setState('timeout')
          return
        }

        setTimeout(poll, POLL_INTERVAL_MS)
      } catch {
        if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
          setState('timeout')
        } else {
          setTimeout(poll, POLL_INTERVAL_MS)
        }
      }
    }

    poll()
    return () => {
      cancelled = true
    }
  }, [state])

  return (
    <div className="max-w-xl mx-auto">
      <div className="card text-center py-12" role="status" aria-live="polite">
        {state === 'pending' && (
          <>
            <Loader2 size={48} className="mx-auto text-primary-600 animate-spin mb-4" aria-hidden="true" />
            <h1 className="text-2xl font-heading font-bold text-gray-900 mb-2">
              מעבד את התשלום...
            </h1>
            <p className="text-gray-600">
              זה לוקח בדרך כלל כמה שניות. אנא אל תסגור את הדף.
            </p>
          </>
        )}

        {state === 'success' && (
          <>
            <CheckCircle2 size={48} className="mx-auto text-green-600 mb-4" aria-hidden="true" />
            <h1 className="text-2xl font-heading font-bold text-gray-900 mb-2">
              התשלום בוצע בהצלחה!
            </h1>
            <p className="text-gray-600 mb-6">
              המסלול שלך שודרג ל-Pro. חשבונית מס/קבלה נשלחה לדוא"ל שלך.
            </p>
            <Link to="/subscription" className="btn btn-primary inline-block">
              להמשך
            </Link>
          </>
        )}

        {state === 'failed' && (
          <>
            <XCircle size={48} className="mx-auto text-red-600 mb-4" aria-hidden="true" />
            <h1 className="text-2xl font-heading font-bold text-gray-900 mb-2">
              התשלום לא הושלם
            </h1>
            <p className="text-gray-600 mb-6">
              משהו השתבש בתהליך התשלום. לא חויבת. ניתן לנסות שוב.
            </p>
            <Link to="/subscription" className="btn btn-primary inline-block">
              חזרה לעמוד המנוי
            </Link>
          </>
        )}

        {state === 'timeout' && (
          <>
            <Loader2 size={48} className="mx-auto text-amber-600 mb-4" aria-hidden="true" />
            <h1 className="text-2xl font-heading font-bold text-gray-900 mb-2">
              עדיין מעבד...
            </h1>
            <p className="text-gray-600 mb-6">
              העדכון לוקח קצת יותר זמן מהרגיל. רענן את עמוד המנוי בעוד דקה
              כדי לראות את הסטטוס המעודכן.
            </p>
            <Link to="/subscription" className="btn btn-primary inline-block">
              לעמוד המנוי
            </Link>
          </>
        )}
      </div>
    </div>
  )
}
